import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MailIcon, MapPinIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { navItems, site } from '../../data/site';
import { useActiveSection } from '../../hooks/useActiveSection';
import { ButtonLink } from '../ui/ButtonLink';
import { Container } from '../ui/Container';
import { MobileMenu } from './MobileMenu';
export function Header() {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const activeId = useActiveSection();
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => mq.matches && setOpen(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return <>
      <a href="#main" className="sr-only z-[60] rounded bg-brand px-4 py-3 font-semibold text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4" data-id="element-289" data-mp="37z2mp2h19gu">
        Skip to content
      </a>

      <div className="on-dark hidden bg-black text-[13px] text-white/70 sm:block" data-id="element-290" data-mp="bui43x9pr88q">
        <Container className="flex h-9 items-center justify-between gap-6" data-id="element-291" data-mp="bsf5wpyeq8sx">
          <a href={site.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white" data-id="element-292" data-mp="gm9sq29b73w8">
            <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-293" data-mp="3k2qela6j2405" />
            {site.street}, {site.cityLine}
            <span className="sr-only" data-id="element-294" data-mp="3p0i6i4769q4c"> (opens directions in a new tab)</span>
          </a>
          <div className="flex items-center gap-6" data-id="element-295" data-mp="3tzv1xdc405ij">
            <a href={site.phoneHref} className="inline-flex items-center gap-2 hover:text-white" data-id="element-296" data-mp="3tz392a9wclvy">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-297" data-mp="1xwu0xiu76y1" />
              {site.phoneDisplay}
            </a>
            <a href={site.emailHref} className="hidden items-center gap-2 hover:text-white md:inline-flex" data-id="element-298" data-mp="19ci7dk4mz662">
              <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-299" data-mp="3acdcjgg2w5yc" />
              {site.email}
            </a>
          </div>
        </Container>
      </div>

      <header className="on-dark sticky top-0 z-50 border-b border-white/10 bg-ink text-white" data-id="element-300" data-mp="35cs8irm1x7er">
        <Container className="flex h-[76px] items-center justify-between gap-6" data-id="element-301" data-mp="3jcjkkmt0cdq4">
          <a href="#top" className="flex shrink-0 items-center gap-3" onClick={close} data-id="element-302" data-mp="3ef1h3bxreqpi">
            <img src={site.logo} alt="" className="h-[60px] w-auto" width={48} height={60} data-id="element-303" data-mp="2qnmgbnlmshgd" />
            <span className="font-display text-[1.35rem] font-bold uppercase leading-[0.95] tracking-wide" data-id="element-304" data-mp="2mnlngxxsc3ps">
              Bates
              <br data-id="element-305" data-mp="30fs3mk0xuap8" />
              Taekwondo
            </span>
          </a>

          <nav aria-label="Primary" className="hidden lg:block" data-id="element-306" data-mp="2vn8txy5dfzpp">
            <ul className="flex items-center gap-9" data-id="element-307" data-mp="27xwiermknfk8">
              {navItems.map(item => {
              const isActive = activeId === item.id;
              return <li key={item.id} data-id="element-308" data-mp="22wqoiqh6dm94">
                    <a href={`#${item.id}`} aria-current={isActive ? 'true' : undefined} className={`relative inline-flex min-h-[44px] items-center text-[15px] font-medium transition-colors duration-150 ${isActive ? 'text-white' : 'text-white/70 hover:text-white'}`} data-id="element-309" data-mp="ig94zt7vlg5x">
                      {item.label}
                      <span aria-hidden="true" className={`absolute inset-x-0 bottom-1.5 h-[2px] origin-left bg-brand transition-transform duration-200 ease-out ${isActive ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-310" data-mp="1259sre30njvp" />
                    </a>
                  </li>;
            })}
            </ul>
          </nav>

          <div className="flex items-center gap-2" data-id="element-311" data-mp="3hvw9yrq2k1qg">
            <ButtonLink href="#first-class" size="md" className="hidden md:inline-flex" data-id="element-312" data-mp="3hwp8x6isdmy1">
              Book a Free Trial Class
            </ButtonLink>
            <button ref={toggleRef} type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} className="inline-flex h-12 w-12 items-center justify-center rounded text-white transition-colors duration-150 hover:bg-white/10 lg:hidden" data-id="element-313" data-mp="3sf2ucyrxd6s7">
              {open ? <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-314" data-mp="fyqvdghlc1lm" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-315" data-mp="aycjfncvc7ib" />}
            </button>
          </div>
        </Container>
        <MobileMenu open={open} onClose={close} activeId={activeId} toggleRef={toggleRef} data-id="element-316" data-mp="5zrljul39zx1" />
      </header>
    </>;
}